import { uniq } from 'lodash';
import SyosEvents from '../utils/SyosEvents';
import SyosServiceMethod from './SyosServiceMethod';
import { invoke } from './SyosApiRequest';
import type CurrencyFormatter from '../../tnew/currency/CurrencyFormatter';
import type SyosController from '../SyosController';
import {
  Screen, GetSeatListResponse, Zone,
  SeatListResult,
  SyosInitDataResponse,
} from './types';
import processPerformanceDetails from './processPerformanceDetails';
import ZonePriceTypeMap from './ZonePriceTypeMap';
import processScreens from './processScreens';
import GeneralComponent from '../components/GeneralComponent';
import processSeats from './processSeats';
import SyosConfig from '../SyosConfig';

interface ReservationSeat {
  selectedPriceTypeId: number;
  id: number;
  zoneId: number;
}

class SyosApiDataService {
  zonePriceTypeMap = new ZonePriceTypeMap([]);

  zones: Map<number, Zone> = new Map();

  screens: Screen[] = [];

  productionSeasonId: number = 0;

  facilityId: number = 0;

  priceTypeIdsString: string;

  constructor(private syosController: SyosController, private performanceId: number, private currencyFormatter: CurrencyFormatter, private config: SyosConfig) {
    this.priceTypeIdsString = uniq(this.config.priceTypeQuantities).join(',');
  }

  async invoke(...args) {
    return invoke(this.syosController, ...args);
  }

  async getInitData() {
    const { performanceId, priceTypeIdsString } = this;

    const data: SyosInitDataResponse = await this.invoke(SyosServiceMethod.GetInitData, { performanceId, priceTypeIds: priceTypeIdsString });

    if (!data.IsSeatMapAvailable) {
      return null;
    }

    if (data.AvailablePrices.length === 0) {
      return null;
    }

    this.productionSeasonId = data.ProductionSeasonId;

    this.facilityId = data.FacilityId;

    const performanceDetails = processPerformanceDetails({
      initDataResponse: data,
      currencyFormatter: this.currencyFormatter,
      feeDisclaimers: this.config.feeDisclaimers,
      isFeeDisclaimerVisible: this.config.isFeeDisclaimerVisible,
      priceRangeSeparator: GeneralComponent.priceRangeSeparator,
      globalPriceRangeIncludesAllPriceTypes: this.config.priceRangeIncludesAllPriceTypes,
      priceTypeSortOrder: this.config.priceTypeSortOrder,
    });

    this.zonePriceTypeMap = performanceDetails.zonePriceTypeMap;

    this.zones = performanceDetails.zones;

    const screens = processScreens({
      screens: data.Screens,
      priceRangeIncludesAllPriceTypes: performanceDetails.priceRangeIncludesAllPriceTypes,
      currencyFormatter: this.currencyFormatter,
      globalScreenButtonShouldDisplayPriceTypeDescription: this.config.screenButtonShouldDisplayPriceTypeDescription,
      globalScreenButtonShouldRenderPriceRange: this.config.screenButtonShouldRenderPriceRange,
      defaultPriceTypeId: performanceDetails.defaultPriceTypeId,
      zonePriceTypeMap: this.zonePriceTypeMap,
      screenZonesMap: performanceDetails.screenZonesMap,
    });

    this.screens = screens;

    this.syosController.emit(SyosEvents.SCREENS_DATA_LOADED, { screens });

    const maximumSeatCount = data.MaxSeats >= 1
      ? data.MaxSeats
      : Infinity;

    return {
      maximumSeatCount,
      screensData: screens,
    };
  }

  getSeatList(screenId: number): Promise<SeatListResult> {
    const { performanceId, facilityId, priceTypeIdsString } = this;

    const promise = new Promise<SeatListResult>((resolve) => {
      const request: Promise<GetSeatListResponse> = this.invoke(SyosServiceMethod.GetSeatList, {
        performanceId, facilityId, screenId, priceTypeIds: priceTypeIdsString,
      }, 'GET', true, false);

      request
        .then((getSeatListResponse) => {
          this.syosController.emit(SyosEvents.API_REQUEST_COMPLETED);

          const { zones } = this;

          const processSeatsResult = processSeats({
            zones,
            getSeatListResponse,
          });

          const screenZones = getSeatListResponse.ScreenZoneList
            .map(({ zone_no }) => this.zones.get(zone_no)!);

          const result: SeatListResult = {
            performanceId,
            facilityId,
            screenId,
            screenZones,
            columnCount: processSeatsResult.columnCount,
            nonSeats: processSeatsResult.nonSeats,
            seats: processSeatsResult.seats,
            rowCount: processSeatsResult.rowCount,
            stageOrientation: processSeatsResult.stageOrientation,
            seatTypes: getSeatListResponse.SeatTypes,
          };

          this.syosController.emit(SyosEvents.SEAT_LIST_DATA_LOADED, result);

          resolve(result);
        })
        .catch((reason) => {
          throw reason;
        });
    });

    return promise;
  }

  async reserveTempCartSeats(seats: ReservationSeat[]) {
    const payload = seats.map((s) => ({
      PerfNo: this.performanceId,
      ProdSeasonId: this.productionSeasonId,
      SeatNo: s.id,
      PriceType: s.selectedPriceTypeId,
      ZoneNo: s.zoneId,
    }));

    const data = this.invoke(SyosServiceMethod.ReserveTempCartSeats, { seats: payload }, 'POST', false);

    return data;
  }

  getScreen(screenId: number) {
    return this.screens.find((screen) => screen.id === screenId);
  }

  getZonePriceTypes(zoneId: number) {
    return this.zonePriceTypeMap.getZonePriceTypes(zoneId);
  }

  getPriceType(zoneId: number, priceTypeId: number) {
    return this.zonePriceTypeMap.getPriceType(zoneId, priceTypeId);
  }
}

export default SyosApiDataService;
